"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreamKitVersionsIndexSchema = exports.STREAMKIT_DOWNLOAD_PAGE = exports.STREAMLAIN_PUBLIC_ORIGIN = exports.STREAMKIT_TARGETS = exports.SHA256SUMS_FILE_NAME = exports.STREAMKIT_BINARY_NAME = exports.DEFAULT_STREAMKIT_RELEASE_CHANNEL = exports.STREAMKIT_RELEASE_CHANNELS = void 0;
exports.streamkitTarget = streamkitTarget;
exports.assertStreamkitChannel = assertStreamkitChannel;
exports.parseStrictSemver = parseStrictSemver;
exports.streamkitReleasePrefix = streamkitReleasePrefix;
exports.latestManifestObjectKey = latestManifestObjectKey;
exports.versionsIndexObjectKey = versionsIndexObjectKey;
exports.releaseObjectKey = releaseObjectKey;
exports.artifactFileName = artifactFileName;
exports.embedFileName = embedFileName;
exports.embedTypesFileName = embedTypesFileName;
exports.streamKitVersionsIndexUrl = streamKitVersionsIndexUrl;
exports.streamKitDownloadUrl = streamKitDownloadUrl;
exports.objectUrl = objectUrl;
const zod_1 = require("zod");
exports.STREAMKIT_RELEASE_CHANNELS = Object.freeze(['stable']);
exports.DEFAULT_STREAMKIT_RELEASE_CHANNEL = 'stable';
exports.STREAMKIT_BINARY_NAME = 'sl-streamkit';
exports.SHA256SUMS_FILE_NAME = 'SHA256SUMS';
const STRICT_SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;
function target(platform, arch, nodeOs, archiveExtension) {
    return Object.freeze({
        id: `${platform}-${arch}`,
        platform,
        arch,
        nodeOs,
        archiveExtension,
        executableExtension: platform === 'windows' ? '.exe' : '',
    });
}
exports.STREAMKIT_TARGETS = Object.freeze([
    target('macos', 'arm64', 'darwin', '.tar.gz'),
    target('macos', 'x64', 'darwin', '.tar.gz'),
    target('linux', 'x64', 'linux', '.tar.xz'),
    target('linux', 'arm64', 'linux', '.tar.xz'),
    target('windows', 'x64', 'win', '.zip'),
]);
function streamkitTarget(id) {
    const found = exports.STREAMKIT_TARGETS.find(candidate => candidate.id === id);
    if (!found) {
        throw new Error(`streamkit target must be one of ${exports.STREAMKIT_TARGETS.map(t => t.id).join(', ')}; got "${id}"`);
    }
    return found;
}
function assertStreamkitChannel(channel) {
    if (!exports.STREAMKIT_RELEASE_CHANNELS.includes(channel)) {
        throw new Error(`streamkit release channel must be one of ${exports.STREAMKIT_RELEASE_CHANNELS.join(', ')}; got "${channel}"`);
    }
    return channel;
}
function parseStrictSemver(version, label = 'version') {
    const match = STRICT_SEMVER_RE.exec(version ?? '');
    if (!match) {
        throw new Error(`${label} must be major.minor.patch; got "${version}"`);
    }
    return match.slice(1, 4).map(Number);
}
function streamkitReleasePrefix(channel) {
    return `streamkit/${assertStreamkitChannel(channel)}`;
}
function latestManifestObjectKey(channel) {
    return `${streamkitReleasePrefix(channel)}/latest.json`;
}
function versionsIndexObjectKey(channel) {
    return `${streamkitReleasePrefix(channel)}/versions.json`;
}
function releaseObjectKey(channel, version, fileName) {
    parseStrictSemver(version);
    return `${streamkitReleasePrefix(channel)}/${version}/${fileName}`;
}
function artifactFileName(version, streamkitTargetEntry) {
    const { arch, executableExtension, platform } = streamkitTargetEntry;
    return `${exports.STREAMKIT_BINARY_NAME}-${version}-${platform}-${arch}${executableExtension}`;
}
/**
 * The file of a release a Node program imports to embed the StreamKit server
 * (`createStreamKitHandler`, `createStreamKitContainerHandler`).
 */
function embedFileName(version) {
    parseStrictSemver(version);
    return `${exports.STREAMKIT_BINARY_NAME}-embed-${version}.cjs`;
}
/** The types of the embed bundle of a release, beside it. */
function embedTypesFileName(version) {
    parseStrictSemver(version);
    return `${exports.STREAMKIT_BINARY_NAME}-embed-${version}.d.ts`;
}
exports.STREAMLAIN_PUBLIC_ORIGIN = 'https://streamlain.de';
exports.STREAMKIT_DOWNLOAD_PAGE = exports.STREAMLAIN_PUBLIC_ORIGIN + '/download';
/** What the Studio reads of the release index of a channel: each release and its package contract. */
exports.StreamKitVersionsIndexSchema = zod_1.z.object({
    releases: zod_1.z.array(zod_1.z.object({
        version: zod_1.z.string(),
        packageContract: zod_1.z.number().int().positive(),
        artifacts: zod_1.z.array(zod_1.z.object({
            kind: zod_1.z.enum(['cli', 'desktop']),
            platform: zod_1.z.enum(['macos', 'linux', 'windows']),
            arch: zod_1.z.enum(['x64', 'arm64']),
            url: zod_1.z.string().url(),
        })),
    })),
});
/** The public address of the release index of a channel. */
function streamKitVersionsIndexUrl(channel = exports.DEFAULT_STREAMKIT_RELEASE_CHANNEL) {
    return objectUrl({
        publicOrigin: exports.STREAMLAIN_PUBLIC_ORIGIN,
        objectKey: versionsIndexObjectKey(channel),
    });
}
/** Only configured targets and a release version can form a program URL. */
function streamKitDownloadUrl({ version, target, channel = exports.DEFAULT_STREAMKIT_RELEASE_CHANNEL, }) {
    const entry = streamkitTarget(target);
    return objectUrl({
        publicOrigin: exports.STREAMLAIN_PUBLIC_ORIGIN,
        objectKey: releaseObjectKey(channel, version, artifactFileName(version, entry)),
    });
}
/** Shared encoding for public release object paths. */
function objectUrl({ publicOrigin, objectKey, }) {
    return (publicOrigin +
        '/' +
        objectKey
            .split('/')
            .map(segment => encodeURIComponent(segment).replaceAll('%2F', '/'))
            .join('/'));
}
